/* Request utility */

"use strict";

import { AuthController } from "@/control/auth";
import axios, { AxiosError } from "axios";

const API_PREFIX = "/api/v1";

export function GetAPIURL(path: string): string {
    if (process.env.NODE_ENV === 'development') {
        return (process.env.VUE_APP_DEV_TEST_HOST || "http://localhost") + API_PREFIX + path;
    } else {
        return (process.env.VUE_APP_API_SERVER_HOST || ".") + API_PREFIX + path;
    }
}

export function GetAssetURL(path: string): string {
    if (process.env.NODE_ENV === 'development') {
        return new URL(path, process.env.VUE_APP_DEV_TEST_HOST || "http://localhost").toString();
    } else {
        const base = new URL(process.env.VUE_APP_ASSETS_SERVER_HOST || ".", location.protocol + "//" + location.host).toString()
        return new URL(path, base).toString();
    }
}

export function GenerateURIQuery(params: any): string {
    const keys = Object.keys(params);
    if (keys.length === 0) {
        return "";
    }

    let result = "";

    for (const key of keys) {
        if (!params[key]) {
            continue;
        }

        if (result !== "") {
            result += "&";
        } else {
            result += "?";
        }

        result += encodeURIComponent(key) + "=" + encodeURIComponent(params[key]);
    }

    return result;
}

/* eslint-disable @typescript-eslint/no-unused-vars */
export interface RequestParams<Return_type = any> {
    method: "GET" | "POST" | "DELETE";
    url: string;
    json?: any;
    form?: FormData;
}

export class Request<Return_type = any> {

    public static pending: { [key: string]: Request } = {};

    public static ErrorHandler(): RequestErrorHandler {
        return new RequestErrorHandler();
    }

    public static Do<Return_type = any>(params: RequestParams<Return_type>): Request<Return_type> {
        if (params.method === "POST") {
            if (params.form) {
                return Request.PostFormData<Return_type>(null, params.url, params.form);
            } else {
                return Request.PostJSON<Return_type>(null, params.url, params.json || {});
            }
        } else if (params.method === "DELETE") {
            return Request.Delete<Return_type>(null, params.url);
        } else {
            return Request.Get<Return_type>(null, params.url);
        }
    }

    public static Pending<Return_type = any>(key: string, params: RequestParams<Return_type>): Request<Return_type> {
        if (params.method === "POST") {
            if (params.form) {
                return Request.PostFormData<Return_type>(key, params.url, params.form);
            } else {
                return Request.PostJSON<Return_type>(key, params.url, params.json || {});
            }
        } else if (params.method === "DELETE") {
            return Request.Delete<Return_type>(key, params.url);
        } else {
            return Request.Get<Return_type>(key, params.url);
        }
    }

    public static Get<Return_type = any>(key: string, url: string): Request<Return_type> {
        Request.Abort(key); // Abort any other request
        const controller = new AbortController();

        const r = new Request<Return_type>(url, controller);

        if (key) {
            Request.pending[key] = r;
        }

        const authToken = AuthController.Session;

        axios.get(url, {
            signal: controller.signal,
            headers: {
                "x-session-id": authToken,
            },
        }).then(response => {
            if (key) {
                delete Request.pending[key];
            }

            r._onSuccess(response.data)
        }).catch(thrown => {
            if (axios.isCancel(thrown)) {
                r._onCancel();
            } else if (axios.isAxiosError(thrown)) {
                if (key) {
                    delete Request.pending[key];
                }
                r._onRequestError(thrown);
            } else {
                if (key) {
                    delete Request.pending[key];
                }
                r._onUnexpectedError(thrown);
            }
        })

        return r;
    }

    public static PostJSON<Return_type = any>(key: string, url: string, json: any): Request<Return_type> {
        Request.Abort(key); // Abort any other request
        const controller = new AbortController();

        const r = new Request<Return_type>(url, controller);

        if (key) {
            Request.pending[key] = r;
        }

        const authToken = AuthController.Session;

        axios.post(url, json, {
            signal: controller.signal,
            headers: {
                "Content-Type": "application/json",
                "x-session-id": authToken,
            },
        }).then(response => {
            if (key) {
                delete Request.pending[key];
            }

            r._onSuccess(response.data)
        }).catch(thrown => {
            if (axios.isCancel(thrown)) {
                r._onCancel();
            } else if (axios.isAxiosError(thrown)) {
                if (key) {
                    delete Request.pending[key];
                }
                r._onRequestError(thrown);
            } else {
                if (key) {
                    delete Request.pending[key];
                }
                r._onUnexpectedError(thrown);
            }
        })

        return r;
    }

    public static PostFormData<Return_type = any>(key: string, url: string, form: FormData): Request<Return_type> {
        Request.Abort(key); // Abort any other request
        const controller = new AbortController();

        const r = new Request<Return_type>(url, controller);

        if (key) {
            Request.pending[key] = r;
        }

        const authToken = AuthController.Session;

        axios.post(url, form, {
            signal: controller.signal,
            headers: {
                "Content-Type": "multipart/form-data",
                "x-session-id": authToken,
            },
            onUploadProgress: progressEvent => {
                r._onUploadProgress(progressEvent.loaded || 0, progressEvent.total || 0);
            }
        }).then(response => {
            if (key) {
                delete Request.pending[key];
            }

            r._onSuccess(response.data)
        }).catch(thrown => {
            if (axios.isCancel(thrown)) {
                r._onCancel();
            } else if (axios.isAxiosError(thrown)) {
                if (key) {
                    delete Request.pending[key];
                }
                r._onRequestError(thrown);
            } else {
                if (key) {
                    delete Request.pending[key];
                }
                r._onUnexpectedError(thrown);
            }
        })

        return r;
    }

    public static Delete<Return_type = any>(key: string, url: string): Request<Return_type> {
        Request.Abort(key); // Abort any other request
        const controller = new AbortController();

        const r = new Request<Return_type>(url, controller);

        if (key) {
            Request.pending[key] = r;
        }

        const authToken = AuthController.Session;

        axios.delete(url, {
            signal: controller.signal,
            headers: {
                "Content-Type": "application/json",
                "x-session-id": authToken,
            },
        }).then(response => {
            if (key) {
                delete Request.pending[key];
            }

            r._onSuccess(response.data)
        }).catch(thrown => {
            if (axios.isCancel(thrown)) {
                r._onCancel();
            } else if (axios.isAxiosError(thrown)) {
                if (key) {
                    delete Request.pending[key];
                }
                r._onRequestError(thrown);
            } else {
                if (key) {
                    delete Request.pending[key];
                }
                r._onUnexpectedError(thrown);
            }
        })

        return r;
    }

    public static Abort(key: string) {
        if (!key) {
            return;
        }
        if (Request.pending[key]) {
            Request.pending[key].abortController.abort();
            delete Request.pending[key];
        }
    }

    public url: string;
    public abortController: AbortController;

    private _onSuccess: (response: any) => void;
    private _onCancel: () => void;
    private _onRequestError: (error: AxiosError) => void;
    private _onUnexpectedError: (error: Error) => void;

    private _onUploadProgress: (loaded: number, total: number) => void;

    constructor(url: string, abortController: AbortController) {
        this.url = url;
        this.abortController = abortController;
        this._onSuccess = function () { };
        this._onCancel = function () { };
        this._onRequestError = function () { };
        this._onUnexpectedError = function () { };
        this._onUploadProgress = function () { };
    }

    public onSuccess(fn: (response: Return_type) => void): Request {
        this._onSuccess = fn;
        return this;
    }

    public onCancel(fn: () => void): Request {
        this._onCancel = fn;
        return this;
    }

    public onRequestError(fn: (err: AxiosError) => void): Request {
        this._onRequestError = fn;
        return this;
    }

    public onUnexpectedError(fn: (err: Error) => void): Request {
        this._onUnexpectedError = fn;
        return this;
    }

    public onUploadProgress(fn: (loaded: number, total: number) => void): Request {
        this._onUploadProgress = fn;
        return this;
    }
}

interface RequestErrorHandlerCallback {
    status: number | string;
    code: string;
    callback: () => void;
}

export class RequestErrorHandler {
    private callbacks: RequestErrorHandlerCallback[];

    constructor() {
        this.callbacks = [];
    }

    public add(status: number | string, code: string, callback: () => void): RequestErrorHandler {
        this.callbacks.push({
            status: status,
            code: code,
            callback: callback,
        });

        return this;
    }

    public handle(error: AxiosError) {
        if (!error.response) {
            return;
        }
        if (error.response.status === 0 && error.response.statusText === "abort") {
            return;
        }

        // Get error code from body

        let errorCode = "";
        const data = error.response.data;
        if (data) {
            try {
                let parsedData: any;

                if (typeof data === "string") {
                    parsedData = JSON.parse(data);
                } else {
                    parsedData = data;
                }

                errorCode = parsedData.error_code || parsedData.code || "";
            } catch (err) {
                errorCode = "";
            }
        }

        for (const callback of this.callbacks) {
            if (callback.status === "*" || callback.status === error.response.status) {
                if (callback.code === "*" || errorCode === callback.code) {
                    callback.callback();
                    return;
                }
            }
        }
    }
}
